import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { Slider } from '../../../dbWebUI'
import { Box, Text } from '@deseretbook/react-ui'

import './BottomToolbar.css'

/**
 * Bottom Toolbar component for use in the Reader Page.
 *
 * @returns {function} Component
 */
function BottomToolbar({
  readerOptions,
  onPrevious,
  onNext,
  className,
  pageCountLabel,
  completionPercentage,
  currentPage,
  totalPages,
  showToolbars,
  handleUpdateToolbars,
  manifest,
  onSelectChapter,
  toc,
  ...rest
}) {
  const [val, setVal] = useState(currentPage)
  const [tooltip, setTooltip] = useState({
    visible: false,
    left: 0,
    page: currentPage,
  })
  const sliderRef = useRef(null)

  const { pageColor = {} } = readerOptions
  const { backgroundColor, color } = pageColor

  const getChapterLabel = (page) => {
    if (!page) return ''

    const pageInfo = manifest.items.pages[page - 1]
    const chapter = toc.bySrc[pageInfo.href]

    return (chapter && chapter.label) || ''
  }

  const handleMouseEnter = () => {
    handleUpdateToolbars(true, true)
  }

  const handleMouseLeave = () => {
    handleUpdateToolbars(true, false)
  }

  const handleAfterChange = (newVal) => {
    const page = manifest.items.pages[newVal - 1]
    const chapter = { src: page.href }
    onSelectChapter(chapter)
    setTooltip({
      visible: false,
      page: val,
    })
  }

  const handleSliderChange = (val) => {
    setVal(val)

    if (sliderRef.current) {
      const sliderRect = sliderRef.current.getBoundingClientRect()
      const percentage = (val - 1) / (totalPages - 1)
      const leftPos = percentage * sliderRect.width

      setTooltip({
        visible: true,
        left: leftPos,
        page: val,
      })
    }
  }

  useEffect(() => {
    setVal(currentPage)
  }, [currentPage])

  const pagesLeft = totalPages - currentPage

  const tooltipLabel = getChapterLabel(tooltip.page)

  return (
    <Box
      className="BottomToolbarContainer"
      isFullwidth
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <Box
        className={`bottom-toolbar ${showToolbars ? '' : 'hidden'}`}
        alignItems="center"
        justifyContent="center"
        isFullwidth
        style={{ backgroundColor, color }}
      >
        <Box isFullwidth style={{ maxWidth: '85vw' }} isColumn alignItems="center">
          <Box alignItems="center" isFullwidth>
            <Text
              className="bottom-toolbar-text"
              style={{
                color,
              }}
            >
              {val}
            </Text>
            <div ref={sliderRef} style={{ position: 'relative', width: '100%' }}>
              <Slider
                value={val}
                min={1}
                max={totalPages}
                step={1}
                onChange={handleSliderChange}
                onAfterChange={handleAfterChange}
              />

              {tooltip.visible && tooltipLabel && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-35px',
                    left: tooltip.left,
                    transform: 'translateX(-50%)',
                    background: 'rgba(0,0,0,0.75)',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    zIndex: 9999,
                  }}
                >
                  {tooltipLabel}
                </div>
              )}
            </div>

            <Text
              className="bottom-toolbar-text"
              style={{
                color,
              }}
            >
              {totalPages}
            </Text>
          </Box>
          <Text style={{ color }}>{pagesLeft || 'No pages'} left in chapter</Text>
        </Box>
      </Box>
    </Box>
  )
}

BottomToolbar.defaultProps = {
  onNext: () => {},
  onPrevious: () => {},
  className: '',
  pageCountLabel: '',
  completionPercentage: 0,
}

BottomToolbar.propTypes = {
  /** Callback for next chapter button */
  onNext: PropTypes.func,
  /** Callback for previous chapter button */
  onPrevious: PropTypes.func,
  /** Additional class names */
  className: PropTypes.string,
  /** The current page of total pages */
  pageCountLabel: PropTypes.string,
  /** The current progress of the book */
  completionPercentage: PropTypes.number,
}

export default BottomToolbar
